import { Checkbox } from '@liveauctioneers/hammer-ui-core/checkbox';
import { Select } from '@liveauctioneers/hammer-ui-core/select';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { difference, union } from 'es-toolkit';
import * as React from 'react';

import { PaginationFilter } from '@/components/Pagination/hammer-pagination/types/PaginationFilter';

import ActionButtons from '../../ActionButtons/ActionButtons';
import { Folder } from '../../Folders/types/Folder';
import { ConversationType } from '../types/Conversation';
import { FilterLabel, labels } from '../types/Label';
import ListItem from './ListItem';
import ResultsPagingStatus from './ResultsPagingStatus';

type ConversationsListProps = {
    readonly activeFolder: number | null;
    readonly archiveLabel: React.ReactNode;
    readonly className?: string;
    readonly conversations: ConversationType[];
    readonly filter: FilterLabel;
    readonly folders: Folder[];
    readonly formatNumber: (num: number) => string;
    readonly getMessengerUrl: (conversationId: number) => string;
    readonly markReadLabel: React.ReactNode;
    readonly markUnreadLabel: React.ReactNode;
    readonly mobile?: boolean;
    readonly moveLabel: React.ReactNode;
    readonly onArchive: (conversationIds: number[]) => void;
    readonly onFilter: (filter: FilterLabel) => void;
    readonly onMarkRead: (conversationIds: number[]) => void;
    readonly onMarkUnread: (conversationIds: number[]) => void;
    readonly onMove: (conversationIds: number[], folder: Folder) => void;
    readonly paginationFilter: PaginationFilter;
    readonly testid?: string;
};

const getOption = (value: ConversationsListProps['filter']) => ({
    label: labels[value],
    value: value,
});

const ConversationsList = ({
    activeFolder,
    archiveLabel,
    className,
    conversations = [],
    filter = 'all',
    folders = [],
    formatNumber,
    getMessengerUrl,
    markReadLabel,
    markUnreadLabel,
    mobile,
    moveLabel,
    onArchive,
    onFilter,
    onMarkRead,
    onMarkUnread,
    onMove,
    paginationFilter,
    testid = 'messengerConversationList',
}: ConversationsListProps) => {
    const [all, setSelectAll] = React.useState(false);
    const [selectedConversations, setSelectedConversations] = React.useState<number[]>([]);
    const [isRead, setReadState]: ['neither' | 'read' | 'unread' | 'both', Function] = React.useState('neither');

    React.useEffect(() => {
        let haveRead = false,
            haveUnread = false,
            result: 'neither' | 'read' | 'unread' | 'both' = isRead;
        conversations
            ?.filter((conversation: ConversationType) => selectedConversations.includes(conversation.conversationId))
            ?.map((conversation) => {
                if (conversation.read) {
                    haveRead = true;
                } else {
                    haveUnread = true;
                }
                return undefined;
            });
        if (!haveRead) {
            if (!haveUnread) {
                result = 'neither';
            } else {
                result = 'read';
            }
        } else {
            if (!haveUnread) {
                result = 'unread';
            } else {
                result = 'both';
            }
        }
        if (result !== isRead) {
            setReadState(result);
        }
    }, [isRead, conversations, selectedConversations]);

    const handleSelect = (e: React.MouseEvent<HTMLInputElement>) => {
        const { name } = e.currentTarget;
        const conversationId = Number(name);
        const checked = selectedConversations.indexOf(conversationId) > -1; //current state, not the desired state.
        if (checked) {
            setSelectedConversations(difference(selectedConversations, [conversationId]));
        } else {
            setSelectedConversations(union(selectedConversations, [conversationId]));
        }
        setSelectAll(false);
    };

    const handleSelectAll = () => {
        const checked = !all;
        setSelectAll(checked);

        if (checked) {
            return setSelectedConversations(conversations.map((convo) => convo.conversationId));
        }
        setSelectedConversations([]);
    };

    const uncheckAll = (action: Function) => {
        action();
        setSelectAll(false);
        setSelectedConversations([]);
    };

    const { page, pageSize, totalRecords } = paginationFilter;

    const onFilterChange = (e: any) => {
        onFilter(e?.value);
    };

    return (
        <div className={className}>
            <table
                className="w-full border-collapse"
                data-testid={testid}
            >
                <thead>
                    <tr>
                        {!mobile && (
                            <th
                                className="w-[42px] min-w-[42px] max-w-[42px] border-b-0 pb-sm"
                                style={{ borderBottom: 'none' }}
                            >
                                <div className="mb-[6px] ml-[9px]">
                                    <Checkbox.Option
                                        checked={all}
                                        labelSize="secondary"
                                        onChange={() => {
                                            handleSelectAll();
                                        }}
                                    >
                                        <span className="sr-only">Select all messages</span>
                                    </Checkbox.Option>
                                </div>
                            </th>
                        )}
                        <th
                            className={cn('border-b-0', mobile ? 'p-0 pb-2xs' : 'pb-sm')}
                            colSpan={mobile ? 1 : 3}
                            style={{ borderBottom: 'none' }}
                        >
                            <div className="flex flex-row items-center justify-between">
                                {!mobile && (
                                    <ActionButtons
                                        activeFolder={activeFolder}
                                        archiveLabel={archiveLabel}
                                        enabled={Boolean(selectedConversations.length)}
                                        folders={folders}
                                        isRead={isRead}
                                        markReadLabel={markReadLabel}
                                        markUnreadLabel={markUnreadLabel}
                                        moveLabel={moveLabel}
                                        onArchive={() => uncheckAll(() => onArchive(selectedConversations))}
                                        onMarkRead={() => uncheckAll(() => onMarkRead(selectedConversations))}
                                        onMarkUnread={() => uncheckAll(() => onMarkUnread(selectedConversations))}
                                        onMove={(folder: Folder) => {
                                            uncheckAll(() => onMove(selectedConversations, folder));
                                        }}
                                    />
                                )}
                                {Boolean(onFilter) && (
                                    <div className="mr-2xs w-[180px]">
                                        <Select
                                            isInsideLabel
                                            labelText="Status:"
                                            onChange={onFilterChange}
                                            options={[
                                                {
                                                    label: 'All',
                                                    value: 'all',
                                                },
                                                {
                                                    label: 'Unread',
                                                    value: 'unread',
                                                },
                                                { label: 'Read', value: 'read' },
                                            ]}
                                            small
                                            value={getOption(filter)}
                                        />
                                    </div>
                                )}
                                <ResultsPagingStatus
                                    formatNumber={formatNumber}
                                    page={page}
                                    // @ts-ignore ! CHECK THIS ! needs further work
                                    pageSize={pageSize}
                                    totalRecords={totalRecords || 0}
                                />
                            </div>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {conversations.map((thread) => (
                        <ListItem
                            getMessengerUrl={getMessengerUrl}
                            handleSelect={handleSelect}
                            key={thread.conversationId}
                            mobile={mobile}
                            selected={selectedConversations.includes(thread.conversationId)}
                            thread={thread}
                        />
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default ConversationsList;
